/**
 * UI Domain Types
 *
 * TypeScript types for the UI domain state.
 */

import type { CheckoutView } from '@/domain/checkout';

export interface ScrollAnchor {
  /** Id of the message pinned as the scroll reference point. */
  messageId: string;
  /** Pixels from the container's top edge to that message's top edge. */
  offsetFromTop: number;
  /** Captured while scrolled to the bottom: restore re-pins to the bottom rather than the (stale) message. */
  atBottom: boolean;
}

export interface UiState {
  autoShowWidget: boolean;
  /**
   * Set when this page's display rules ask for the compact button in place of the chat
   * bar. Separate from `suppressedByRules` rather than folded into it: that one means
   * "no entry point at all" and is what `Gladly.show()` clears to make chat reachable,
   * while this only chooses the face an entry point that IS showing wears. Never
   * persisted; retaken per page and per route by evaluateDisplayRules.
   */
  buttonOnlyByRules: boolean;
  chatLastViewedAt: string | null; // ISO string
  checkout: CheckoutView | null;
  composerFocused: boolean;
  displayMode: DisplayMode;
  messagePreviewDismissedAt: string | null; // ISO string
  minimizedAt: string | null; // ISO string
  openedFromLoader: boolean;
  paymentFormOpen: boolean;
  /**
   * Set while a live conversation is overriding this page's display rules, and
   * held for the rest of the page view so the widget cannot be yanked away
   * mid-read when that conversation ends. Never persisted; cleared by
   * pageLoaded, so the rules govern again on the next page or route.
   */
  pinnedByConversation: boolean;
  previousDisplayMode: DisplayMode | null;
  scrollAnchor: ScrollAnchor | null;
  suppressedByRules: boolean;
  transitionState: TransitionState | null;
}

export const DisplayMode = {
  Fullscreen: 'Fullscreen',
  Minimized: 'Minimized',
  Panel: 'Panel',
} as const;

export type DisplayMode = (typeof DisplayMode)[keyof typeof DisplayMode];

export const TransitionPhases = {
  Active: 'Active',
  Appear: 'Appear',
  Done: 'Done',
  Enter: 'Enter',
  Exit: 'Exit',
} as const;

export type TransitionPhase = (typeof TransitionPhases)[keyof typeof TransitionPhases];

export interface TransitionState {
  from: DisplayMode;
  phase: TransitionPhase;
  to: DisplayMode;
}

export function getInitialUiState(): UiState {
  return {
    autoShowWidget: false,
    buttonOnlyByRules: false,
    chatLastViewedAt: null,
    checkout: null,
    composerFocused: false,
    displayMode: DisplayMode.Panel,
    messagePreviewDismissedAt: null,
    minimizedAt: null,
    openedFromLoader: false,
    paymentFormOpen: false,
    pinnedByConversation: false,
    previousDisplayMode: null,
    scrollAnchor: null,
    suppressedByRules: false,
    transitionState: null,
  };
}
